<template>
  <div class="d-flex flex-column" :style="{ gap: '10px' }">
    <v-toolbar flat>
      <v-toolbar-action>
        <div class="d-flex" :style="{ gap: '10px' }">

          <v-btn color="green" @click="e => createDialog = { open: true }">
            <v-icon>mdi-plus</v-icon>
            Create Consumer
          </v-btn>
          <v-btn text @click="$router.push('/consumer-ticket-requests')">
            <v-icon>mdi-ticket</v-icon>
            Consumer Ticket Requests
          </v-btn>
          <export-consumer-loan />
        </div>
      </v-toolbar-action>
    </v-toolbar>
    <!-- Filters & Actions -->
    <div class="d-flex flex-column flex-sm-row mb-3 align-center" :style="{ gap: '10px' }">
      <v-switch label="All Consumers" :default-target="true" :true-value="true" :false-value="false"
        v-model="tableFilters.noEmpty" />
      <v-text-field outlined label="Search Consumers" dense clearable hide-details v-model="tableFilters.search"
        @keyup.enter="v => tableFilters = { ...tableFilters }" @change="v => tableFilters = { ...tableFilters }" />
      <v-select :value="tableFilters.status" @change="v => tableFilters = { ...tableFilters, status: v }" outlined
        :items="statusOptions" label="Filter by Status" dense clearable multiple chips small-chips deletable-chips
        hide-details />

      <v-btn color="primary" @click="fetchConsumers">
        Refresh
      </v-btn>
    </div>

    <!-- Table -->
    <v-data-table :loading="table.loading" :headers="table.headers" :items="table.items">
      <template #item.balance="{ value }">
        <td class="text-end">
          {{ formatAccounting(value || 0) }}
        </td>
      </template>
      <template #item.remarks="{ value }">
        <v-chip
          :color="({ 'paid': 'green', 'not due': 'blue', 'almost due': 'orange', 'due today': 'amber', 'overdue': 'red' })[value] || 'grey'"
          dark small>
          {{ value || 'no loans' }}
        </v-chip>
      </template>
      <template #item.contactNo1="{ value }">
        <td class="text-center">{{ value }}</td>
      </template>
      <template #item.contactNo2="{ value }">
        <td class="text-center">{{ value }}</td>
      </template>
      <template #item._config="{ item }">
        <div class="flex-row d-flex" :style="{ 'gap': '4px' }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="mx-2" fab dark small v-bind="attrs" v-on="on" color="yellow darken-2"
                @click="editConsumer(item)" v-if="authPermissions.has('consumers:edit')">
                <v-icon dark>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>Edit Consumer</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }" v-if="authPermissions.has('consumer_loans:view')">
              <v-btn class="mx-2" fab dark small v-bind="attrs" v-on="on" color="green"
                @click="$router.push({ name: 'Consumer.Loans', params: { consumer: item.id } })">
                <v-icon dark>mdi-clipboard-multiple</v-icon>
              </v-btn>
            </template>
            <span>Manage Loans</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }" v-if="authPermissions.has('consumer_loans:view')">
              <v-btn class="mx-2" fab dark small v-bind="attrs" v-on="on" color="purple"
                @click="$router.push({ name: 'Consumers.Transactions', params: { consumer: item.id } })">
                <v-icon dark>mdi-swap-horizontal</v-icon>
              </v-btn>
            </template>
            <span>Transactions</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }" v-if="authPermissions.has('consumer_loans:view')">
              <v-btn v-if="item.hasAccount" class="mx-2" fab dark small v-bind="attrs" v-on="on" color="yellow darken-2"
                @click="openEditConsumerUser(item.id)">
                <v-icon dark>mdi-account-edit</v-icon>
              </v-btn>
              <v-btn v-else class="mx-2" fab dark small v-bind="attrs" v-on="on" color="blue"
                @click="openCreateConsumerUser(item.id)">
                <v-icon dark>mdi-account</v-icon>
              </v-btn>
            </template>
            <span v-if="item.hasAccount">Edit Account</span>
            <span v-else>Add Account</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>
    <v-dialog v-model="editDialog.open" width="400px" persistent eager>
      <v-card width="400px">
        <v-toolbar color="warning">
          <v-toolbar-title class="black--text">Update Consumer</v-toolbar-title>
        </v-toolbar>
        <edit-consumer ref="editConsumerRef" @close="closeEditConsumer" @success="fetchConsumers" />
      </v-card>
    </v-dialog>
    <v-dialog v-model="createDialog.open" width="400px" persistent eager>
      <v-card width="400px">
        <v-toolbar color="success">
          <v-toolbar-title>New Consumer</v-toolbar-title>
        </v-toolbar>
        <create-consumer ref="createConsumerRef" @close="closeCreateConsumer" @success="fetchConsumers" />
      </v-card>
    </v-dialog>
    <v-dialog v-model="consumerUserEditDialog.open" width="400px" persistent eager>
      <v-card width="400px">
        <v-toolbar color="warning">
          <v-toolbar-title class="black--text">Edit Account</v-toolbar-title>
        </v-toolbar>
        <edit-consumer-user ref="editConsumerUserRef" @close="closeEditConsumerUser" @success="fetchConsumers" />
      </v-card>
    </v-dialog>
    <v-dialog v-model="consumerUserCreateDialog.open" width="400px" persistent eager>
      <v-card width="400px">
        <v-toolbar color="success">
          <v-toolbar-title>Create Account</v-toolbar-title>
        </v-toolbar>
        <create-consumer-user ref="createConsumerUserRef" @close="closeCreateConsumerUser" @success="fetchConsumers" />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { authAxios } from '../../../../../extra-functions/axios.extra'
import { ErrorPopup, LoadingPopup, formatAccounting, useWatchEffect } from '../../../../../lib/utils'

const [watchEffect, clearEffect] = useWatchEffect()

export default {
  name: 'ConsumerIndexV2',
  components: {
    EditConsumer: () => import('./Edit.vue'),
    CreateConsumer: () => import('./Create.vue'),
    CreateConsumerUser: () => import('./CreateUser.vue'),
    EditConsumerUser: () => import('./EditUser.vue'),
    ExportConsumerLoan: () => import('./ExportLoan.vue'),
  },
  data() {
    return {
      editDialog: {
        open: false,
        item: null,
      },
      createDialog: {
        open: false,
      },
      consumerUserCreateDialog: {
        open: false,
      },
      consumerUserEditDialog: {
        open: false,
      },
      table: {
        headers: [
          { text: 'Name', value: 'name' },
          { text: 'Address', value: 'address' },
          { text: 'Contact #1', value: 'contactNo1' },
          { text: 'Contact #2', value: 'contactNo2' },
          { text: 'Balance', value: 'balance', sortable: false },
          { text: 'Remarks', value: 'remarks', sortable: true },
          { text: 'Config', value: '_config', sortable: false },
        ],
        loading: false,
        page: 1,
        itemsPerPage: 10,
        total: 0,
        items: [],
      },
      tableFilters: {
        noEmpty: true,
        search: null,
        status: [
          'paid',
          'not due',
          'almost due',
          'due today',
          'overdue',
        ],
      },
      statusOptions: [
        'paid',
        'not due',
        'almost due',
        'due today',
        'overdue',
      ],
    }
  },
  computed: {
    ...mapState({
      authPermissions: state => state.auth.userPermissions,
    }),
  },
  created() {
    this.fetchConsumers()
  },
  destroyed() {
    clearEffect()
  },
  watch: {
    tableFilters() {
      this.fetchConsumers()
    },
  },
  methods: {
    fetchConsumers() {
      watchEffect(async (onCleanup) => {
        try {
          this.table = { ...this.table, loading: true }
          const controller = new AbortController()
          onCleanup(() => controller.abort())
          const response = await authAxios().get('/v2/consumers', {
            params: { ...this.tableFilters, noEmpty: Number(this.tableFilters.noEmpty) },
            signal: controller.signal,
          })
          this.table = {
            ...this.table,
            items: response.data.data,
            total: response.data.count,
          }
        } catch (e) {
          console.error(e)
          LoadingPopup.close()
          ErrorPopup.fire('Error', 'Oops something went wrong')
        } finally {
          this.table = { ...this.table, loading: false }
        }
      }, 'ConsumerIndexV2_fetchConsumers')
    },
    editConsumer(item) {
      this.editDialog = { open: true, item }
      this.$refs?.editConsumerRef.setConsumer?.(item)
    },
    closeEditConsumer() {
      this.editDialog = { open: false, item: null }
    },
    closeCreateConsumer() {
      this.createDialog = { open: false }
    },
    closeCreateConsumerUser() {
      this.consumerUserCreateDialog = { open: false }
    },
    closeEditConsumerUser() {
      this.consumerUserEditDialog = { open: false }
    },
    openCreateConsumerUser(consumerId) {
      this.$refs.createConsumerUserRef?.setConsumerId?.(consumerId)
      this.consumerUserCreateDialog = { open: true }
    },
    openEditConsumerUser(consumerId) {
      this.$refs.editConsumerUserRef?.setConsumerId?.(consumerId)
      this.$refs.editConsumerUserRef?.fetchConsumerUser?.()
      this.consumerUserEditDialog = { open: true }
    },
    formatAccounting,
  },
}
</script>